import styled from "styled-components";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import { useSearchParams } from "react-router-dom";
import { useFetchEntities } from "@/reactQueryHooks";

//------------------------------------------------------------------------

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px;
  width: 350px;
  gap: 16px;
`;

const TopButtonsRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  cursor: pointer;
`;

//-----------------------------------------------------------------

interface User {
  _id: string;
  name: string;
}

interface ProductOption {
  _id: string;
  title: string;
  productTitle: string;
  productId: string;
}

const OptionsDrawerContent = ({
  close,
}: {
  close: (e: React.MouseEvent) => void;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: entitiesData } = useFetchEntities() as {
    data: { data: any };
  };

  const onlyUnassigned = searchParams.get("onlyUnassigned") === "true";

  // Get user IDs from search params
  const userIdsParam = searchParams.get("userIds");
  const selectedUserIds = userIdsParam ? userIdsParam.split(",") : [];

  // Get product option IDs from search params
  const productOptionIdsParam = searchParams.get("productOptionIds");
  const selectedProductOptionIds = productOptionIdsParam
    ? productOptionIdsParam.split(",")
    : [];

  // Get users from entities and sort by name
  const users: User[] = (entitiesData?.data?.staff || []).sort(
    (a: User, b: User) => a.name.localeCompare(b.name)
  );

  // Find selected users
  const selectedUsers = users.filter((user) =>
    selectedUserIds.includes(user._id)
  );

  // Get all product options from all products
  const allProductOptions: ProductOption[] = (entitiesData?.data?.products || [])
    .flatMap((product: any) =>
      (product.options || []).map((option: any) => ({
        _id: option._id,
        title: option.title,
        productTitle: product.title || `BOKUN #${product.bokun_product_code}`,
        productId: product._id,
      }))
    )
    .sort((a: ProductOption, b: ProductOption) => {
      // Sort by product title first, then by option title
      const productCompare = a.productTitle.localeCompare(b.productTitle);
      if (productCompare !== 0) return productCompare;
      return a.title.localeCompare(b.title);
    });

  // Find selected product options
  const selectedProductOptions = allProductOptions.filter((option) =>
    selectedProductOptionIds.includes(option._id)
  );

  // Check if we have users selected to disable the switch
  const hasUsersSelected = selectedUsers.length > 0;

  const handleUserSelectionChange = (
    _event: React.SyntheticEvent<Element, Event>,
    newSelectedUsers: User[]
  ) => {
    const newParams = new URLSearchParams(searchParams);

    if (newSelectedUsers.length > 0) {
      const userIds = newSelectedUsers.map((user) => user._id).join(",");
      newParams.set("userIds", userIds);
      // When at least one user is selected, set onlyUnassigned to false
      newParams.set("onlyUnassigned", "false");
    } else {
      newParams.delete("userIds");
    }

    setSearchParams(newParams);
  };

  const handleProductOptionSelectionChange = (
    _event: React.SyntheticEvent<Element, Event>,
    newSelectedOptions: ProductOption[]
  ) => {
    const newParams = new URLSearchParams(searchParams);

    if (newSelectedOptions.length > 0) {
      const productOptionIds = newSelectedOptions
        .map((option) => option._id)
        .join(",");
      newParams.set("productOptionIds", productOptionIds);
    } else {
      newParams.delete("productOptionIds");
    }

    setSearchParams(newParams);
  };

  return (
    <Container>
      <TopButtonsRow>
        <IconButton onClick={close} size="small">
          <CloseIcon />
        </IconButton>
      </TopButtonsRow>

      <FormGroup>
        <Tooltip
          title={
            hasUsersSelected
              ? "Cannot show only unassigned when users are selected"
              : ""
          }
          placement="top"
        >
          <FormControlLabel
            control={
              <Switch
                checked={onlyUnassigned}
                disabled={hasUsersSelected}
                onChange={(e) => {
                  if (hasUsersSelected) return; // Extra safety check
                  const newParams = new URLSearchParams(searchParams);
                  newParams.set(
                    "onlyUnassigned",
                    e.target.checked ? "true" : "false"
                  );
                  setSearchParams(newParams);
                }}
              />
            }
            label="Show only unassigned"
          />
        </Tooltip>
      </FormGroup>

      <Autocomplete
        multiple
        options={users}
        value={selectedUsers}
        onChange={handleUserSelectionChange}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, value) => option._id === value._id}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            size="small"
            placeholder="Select users to filter by..."
            helperText="Only show assignments for selected users"
          />
        )}
        size="small"
        sx={{
          "& .MuiOutlinedInput-root": {
            fontSize: "14px",
          },
          "& .MuiInputLabel-root": {
            fontSize: "14px",
          },
          "& .MuiFormHelperText-root": {
            fontSize: "12px",
          },
        }}
      />

      <Autocomplete
        multiple
        options={allProductOptions}
        value={selectedProductOptions}
        onChange={handleProductOptionSelectionChange}
        getOptionLabel={(option) => `${option.productTitle} - ${option.title}`}
        isOptionEqualToValue={(option, value) => option._id === value._id}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            size="small"
            placeholder="Select product options to filter by..."
            helperText="Only show assignments for selected product options"
          />
        )}
        size="small"
        sx={{
          "& .MuiOutlinedInput-root": {
            fontSize: "14px",
          },
          "& .MuiInputLabel-root": {
            fontSize: "14px",
          },
          "& .MuiFormHelperText-root": {
            fontSize: "12px",
          },
        }}
      />
    </Container>
  );
};

export default OptionsDrawerContent;
